import {
  Contact_Create,
  Contact_Read_Nested,
  Contact_Update,
} from '@treadinc/horizon-api-spec';

import { formatPhoneForDisplay, formatPhoneForSubmit } from '~utils/utilFunctions';

// The shape CompanyForm keeps a contact in. `contacts` has a single `name` column
// backend-side; the first/last split is presentational only (see `extractNames` in
// CompanyForm.tsx, which produces these values, and `deparseCompany`, which rejoins them).
export interface CompanyContactFormValue {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export class ContactItem {
  public static parse(proto: Contact_Read_Nested) {
    return new ContactItem(
      proto.id ?? '',
      proto?.name ?? '',
      proto?.email ?? '',
      proto?.phone ?? '',
    );
  }

  public static deparse(proto: ContactItem) {
    const result: Contact_Create = {
      name: proto.name?.length ? proto.name : undefined,
      email: proto.email?.length ? proto.email : undefined,
      phone: proto.phone?.length ? (proto.phone || '').replace(/[^+\d]/g, '') : undefined,
    };

    return result;
  }

  /**
   * Serializes a company's primary or billing contact for `Company_Create` /
   * `Company_Update`. This is the ONLY company-contact serializer — Settings >
   * General, Admin > Companies, and Promote Account all route through it.
   *
   * It accepts both shapes the company forms hand us — a parsed `ContactItem`
   * (single `name`) and a `CompanyForm` value (`firstName` + `lastName`) — and
   * resolves them to one name before a single shared emit path. Calling the
   * `ContactItem`-shaped serializer with a form value read `proto.name`, found
   * nothing, and dropped the key: that is CUS-447, and it stayed invisible
   * because `email`/`phone` happen to exist on both shapes.
   *
   * Every field is emitted unconditionally, blank included. `Contact_Update`
   * types all three as `string` (never null), and the backend's
   * `strip_attributes` turns `''` into `NULL` — so `''` is the only way to clear
   * one. Omitting the key instead leaves the stored value untouched, which is
   * why a contact's email could be edited but never cleared. A blank name is
   * rejected loudly by `Company#primary_contact_required_fields` rather than
   * silently reverting.
   */
  public static deparseCompany(proto: ContactItem | CompanyContactFormValue) {
    const name =
      proto instanceof ContactItem
        ? proto.name
        : `${proto.firstName ?? ''} ${proto.lastName ?? ''}`;

    const result: Contact_Create & Contact_Update = {
      name: name.trim(),
      email: proto.email?.trim() ?? '',
      phone: formatPhoneForSubmit(proto.phone) ?? '',
    };

    return result;
  }

  public get name() {
    return this._name;
  }
  public set name(value: string) {
    this._name = value;
  }

  public get email() {
    return this._email;
  }
  public set email(value: string) {
    this._email = value;
  }

  public get id() {
    return this._id;
  }
  public set id(value: string) {
    this._id = value;
  }

  public get phone() {
    return formatPhoneForDisplay(this._phone);
  }
  public set phone(value: string) {
    this._phone = value;
  }

  constructor(
    private _id: string,
    private _name: string,
    private _email: string,
    private _phone: string,
  ) {}
}
